"use client";
import React, { useRef, useEffect, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

const initialPosts = [
  {
    id: 2,
    permalink: "https://www.instagram.com/reel/DPECO1ijbue/",
  },
  {
    id: 4,
    permalink: "https://www.instagram.com/reel/DO8hQpvkUMq/",
  },
  {
    id: 5,
    permalink: "https://www.instagram.com/p/DS12i9WEZ_N/",
  },
  {
    id: 6,
    permalink: "https://www.instagram.com/reel/DSwkgQKEcbX/",
  },
  {
    id: 7,
    permalink: "https://www.instagram.com/reel/DSr1pO2ERdr/",
  },
];

const caption =
  "Growth isn't always big or loud. 📢 Sometimes, it's a silent shift, a thought, a moment, a nudge. The kind that stays with you long after you're back home. 🏡";
const hashtags =
  "#GrowWithUs #OutcomeOuting #travelwithpurpose #communitytravel #trending #trendingpost #explorepage";

const InstagramFeedSection = () => {
  const cardRefs = useRef([]);
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(null);
  const [visiblePosts, setVisiblePosts] = useState(initialPosts);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);

    const loadScript = () => {
      if (!window.instgrm) {
        const script = document.createElement("script");
        script.src = "//www.instagram.com/embed.js";
        script.async = true;
        document.body.appendChild(script);
        script.onload = () => {
          if (window.instgrm) window.instgrm.Embeds.process();
        };
      } else {
        window.instgrm.Embeds.process();
      }
    };

    loadScript();

    const cleanupInterval = setInterval(() => {
      const embedFrames = document.querySelectorAll("iframe.instagram-media-rendered");
      embedFrames.forEach((frame) => {
        try {
          if (frame.height < 100) {
            const container = frame.closest('.instagram-card-wrapper');
            if (container) {
              const id = parseInt(container.getAttribute('data-id'));
              setVisiblePosts((prev) => prev.filter((p) => p.id !== id));
            }
          }
        } catch (e) {}
      });

      const blockquotes = document.querySelectorAll("blockquote.instagram-media");
      blockquotes.forEach((bq) => {
        if (bq.innerText.includes("Post isn't available") || bq.innerText.includes("broken")) {
          const container = bq.closest('.instagram-card-wrapper');
          if (container) {
            const id = parseInt(container.getAttribute('data-id'));
            setVisiblePosts((prev) => prev.filter((p) => p.id !== id));
          }
        }
      });
    }, 2000);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearInterval(cleanupInterval);
    };
  }, []);

  const scale =
    windowWidth == null
      ? 1
      : windowWidth < 640
      ? 0.86
      : windowWidth < 768
      ? 0.92
      : windowWidth < 1024
      ? 0.98
      : 1;

  const cardWidth =
    windowWidth == null ? "360px" : windowWidth < 1024 ? "326px" : "360px";

  const scrollToCard = (index) => {
    if (!scrollContainerRef.current || !cardRefs.current[index]) return;
    scrollContainerRef.current.scrollTo({
      left: cardRefs.current[index].offsetLeft,
      behavior: "smooth",
    });
    setCurrentIndex(index);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || isDragging) return;
    const container = scrollContainerRef.current;
    const center = container.scrollLeft + container.offsetWidth / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    cardRefs.current.forEach((card, index) => {
      if (!card) return;
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(center - cardCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    setCurrentIndex(closestIndex);
  }, [isDragging]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  return (
    <section className="w-full bg-[#FFFFFFE6] py-6 sm:py-8 md:py-12">
      <motion.h2
        className="text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#FFAE00] tracking-[-0.5px] px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        We Are Now Social
      </motion.h2>

      <div className="relative mt-6 sm:mt-8 md:mt-12 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={scrollContainerRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing pb-4 snap-x snap-mandatory"
          style={{ WebkitOverflowScrolling: "touch" }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
        >
          <AnimatePresence>
            {visiblePosts.map((post, index) => (
              <motion.div
                key={post.id}
                ref={(el) => (cardRefs.current[index] = el)}
                className="instagram-card-wrapper flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px] lg:w-[360px] snap-center"
                data-id={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, width: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <div className="bg-white rounded-xl overflow-hidden border border-[#0D0D0D]/20 transition-all duration-300 hover:shadow-[0_6px_25px_rgba(255,174,0,0.3)] h-[450px] sm:h-[480px] lg:h-[520px] flex flex-col relative">
                  <a
                    href="https://www.instagram.com/outcome_outing/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-4 left-1/2 -translate-x-1/2 z-10"
                  >
                    <div className="w-4 h-4 relative">
                      <Image src="/insta.svg" alt="Instagram Icon" fill className="object-contain" />
                    </div>
                  </a>

                  <div
                    className="relative flex-grow overflow-hidden"
                    style={{
                      transform: `scale(${scale})`,
                      width: cardWidth,
                      transformOrigin: "top left",
                    }}
                  >
                    <blockquote
                      className="instagram-media"
                      data-instgrm-captioned
                      data-instgrm-permalink={post.permalink}
                      data-instgrm-version="14"
                      style={{
                        background: "#FFF",
                        border: 0,
                        borderRadius: "3px",
                        boxShadow: "none",
                        margin: "1px",
                        maxWidth: "540px",
                        minWidth: "326px",
                        padding: 0,
                        width: "calc(100% - 2px)",
                      }}
                    ></blockquote>

                    <a
                      href="https://www.instagram.com/outcome_outing/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-4 right-4 bg-[#FFAE00] text-[#0D0D0D] text-xs sm:text-sm font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-[#E69A00] transition-all duration-300"
                      style={{
                        transform: `scale(${windowWidth < 640 ? 1 / 0.86 : windowWidth < 768 ? 1 / 0.92 : windowWidth < 1024 ? 1 / 0.98 : 1})`,
                        transformOrigin: "bottom right",
                      }}
                    >
                      Follow Us
                    </a>
                  </div>

                  <div className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6 pt-2 flex flex-col">
                    <p className="text-sm sm:text-base leading-relaxed line-clamp-3 text-[#0D0D0D]">
                      {caption}
                    </p>
                    <p className="text-xs sm:text-sm text-[#0081FF] mt-2 line-clamp-1">
                      {hashtags}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="flex justify-center items-center gap-6 mt-6 sm:mt-8">
          <div className="hidden sm:flex items-center gap-4">
            <button
              onClick={() => scrollToCard(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFAE00] hover:bg-[#E69A00] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:scale-100 disabled:opacity-50"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 fill-[#0D0D0D]" viewBox="0 0 24 24">
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
              </svg>
            </button>

            <div className="flex gap-2">
              {visiblePosts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToCard(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "bg-[#FFAE00] w-4 sm:w-6"
                      : "bg-[#0D0D0D]/30 hover:bg-[#0D0D0D]/50"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => scrollToCard(Math.min(visiblePosts.length - 1, currentIndex + 1))}
              disabled={currentIndex === visiblePosts.length - 1}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFAE00] hover:bg-[#E69A00] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:scale-100 disabled:opacity-50"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 fill-[#0D0D0D]" viewBox="0 0 24 24">
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <motion.div
        className="mt-6 sm:mt-8 md:mt-12 h-auto border-t border-b border-[#0D0D0D]/20 bg-[#083258] flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.8 }}
      >
        <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold mb-4">
          Designed for the bold – not the basic.
        </h2>
        <p className="text-white text-sm sm:text-base md:text-lg max-w-4xl leading-relaxed">
          <strong className="font-semibold">
            Outcome Outing is built for Founders, Creators, Students,
            Professionals, Rebels, Dreamers, Doers, and anyone choosing clarity
            over comfort.
          </strong>{" "}
          This isn&rsquo;t a tour. It&rsquo;s a transformation – for those who
          want more than selfies and surface talk.
          <br className="hidden sm:block" />
          <span className="italic block mt-2">✨ We are not for everyone.</span>
        </p>
      </motion.div>

      <style jsx>{`
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .snap-x { scroll-snap-type: x mandatory; }
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .instagram-media { border: none !important; box-shadow: none !important; }
      `}</style>
    </section>
  );
};

export default InstagramFeedSection;